/**
 * Keyboard shortcut catalogue shown in Help & feedback → Keyboard shortcuts.
 * Key bindings themselves come from ShortcutsKeymap (via ShortcutRenderer); this file only
 * names and groups them.
 */
export type ShortcutCategory = 'Chart' | 'Trading' | 'Alerts' | 'Watchlist' | 'Screener' | 'Indicators and drawings'

export type ShortcutEntry = {
    title: string
    /** Keymap category the binding is registered under (informational). */
    category: string
    shortcutName: string
}

const SHORTCUT_DATA: Record<ShortcutCategory, { shortcuts: ShortcutEntry[] }> = {
    Chart: {
        shortcuts: [
            {
                title: 'Quick Search (Symbol)',
                category: 'QUICK_SEARCH',
                shortcutName: 'quickSearch'
            },
            {
                title: 'Open indicators',
                category: 'Chart',
                shortcutName: 'openIndicatorDialog'
            },
            {
                title: 'Load Chart Layout',
                category: 'Chart',
                shortcutName: 'openMultiPaneMenu'
            },
            {
                title: 'Save Chart Layout',
                category: 'Chart',
                shortcutName: 'saveLayout'
            },
            {
                title: 'Change Symbol',
                category: 'SECURITY_SEARCH',
                shortcutName: 'Any letter key'
            },
            {
                title: 'Change Interval',
                category: 'Chart',
                shortcutName: 'openCustomTimeBucketIntervalDialog'
            },
            {
                title: 'Move chart 1 bar to the left',
                category: 'Chart',
                shortcutName: 'moveChartLeft'
            },
            {
                title: 'Move chart 1 bar to the right',
                category: 'Chart',
                shortcutName: 'moveChartRight'
            },
            {
                title: 'Zoom in',
                category: 'Chart',
                shortcutName: 'zoomIn'
            },
            {
                title: 'Zoom out',
                category: 'Chart',
                shortcutName: 'zoomOut'
            },
            {
                title: 'Move to First Bar',
                category: 'Chart',
                shortcutName: 'moveToFirstBar'
            },
            {
                title: 'Move to Last Bar',
                category: 'Chart',
                shortcutName: 'moveToLastBar'
            },
            {
                title: 'Replay Play/Pause',
                category: 'Chart',
                shortcutName: 'replayPlayPause'
            },
            {
                title: 'Move to Realtime Bar',
                category: 'Chart',
                shortcutName: 'moveToRealtimeBar'
            },
            {
                title: 'Undo',
                category: 'Chart',
                shortcutName: 'undo'
            },
            {
                title: 'Redo',
                category: 'Chart',
                shortcutName: 'redo'
            },
            {
                title: 'Toggle Maximize Chart',
                category: 'Chart',
                shortcutName: 'toggleMaximizeChart'
            },
            {
                title: 'Add alert',
                category: 'Chart',
                shortcutName: 'openAlertDialog'
            },
            {
                title: 'Go to date',
                category: 'Chart',
                shortcutName: 'openChartDuration'
            },
            {
                title: 'Save Chart Image',
                category: 'Chart',
                shortcutName: 'saveChartImage'
            },
            {
                title: 'Add Text Note',
                category: 'Chart',
                shortcutName: 'addTextNote'
            },
            {
                title: 'Reset chart view',
                category: 'Chart',
                shortcutName: 'resetChartView'
            },
            {
                title: 'Add symbol to active watchlist',
                category: 'Chart',
                shortcutName: 'addToWatchlist'
            },
            {
                title: 'Change chart type',
                category: 'Chart',
                shortcutName: 'openChartTypeMenu'
            },
            {
                title: 'Split horizontal',
                category: 'Chart',
                shortcutName: 'splitHorizontal'
            },
            {
                title: 'Split vertical',
                category: 'Chart',
                shortcutName: 'splitVertical'
            },
            {
                title: 'Delete current widget',
                category: 'Chart',
                shortcutName: 'deleteCurrentWidget'
            },
            {
                title: 'Pan Left (1 bar)',
                category: 'Chart',
                shortcutName: 'panLeft'
            },
            {
                title: 'Pan Right (1 bar)',
                category: 'Chart',
                shortcutName: 'panRight'
            },
            {
                title: 'Pan Left Fast',
                category: 'Chart',
                shortcutName: 'panLeftFast'
            },
            {
                title: 'Pan Right Fast',
                category: 'Chart',
                shortcutName: 'panRightFast'
            },
            {
                title: 'Scroll to Recent Bar',
                category: 'Chart',
                shortcutName: 'scrollToRecentBar'
            },
            {
                title: 'Replay Step Forward',
                category: 'Chart',
                shortcutName: 'replayStepForward'
            },
            {
                title: 'Switch to Next Chart',
                category: 'Chart',
                shortcutName: 'switchNextChart'
            },
            {
                title: 'Switch to Previous Chart',
                category: 'Chart',
                shortcutName: 'switchPrevChart'
            },
            {
                title: 'Open Keyboard Shortcuts Help',
                category: 'Chart',
                shortcutName: 'openKeyboardShortcutsHelp'
            },
            {
                title: 'Show Plus button on cursor',
                category: 'Chart',
                shortcutName: 'showPlusButtonOnCursor'
            },
            {
                title: 'Open layouts save-menu',
                category: 'SAVE_MENU',
                shortcutName: 'openSaveMenu'
            },
            {
                title: 'Fullscreen mode',
                category: 'Chart',
                shortcutName: 'showHideFullScreen'
            },
        ]
    },
    Trading: {
        shortcuts: [
            {
                title: 'Open/close Trade Panel',
                category: 'Trading',
                shortcutName: 'openCloseTradePanel'
            },
            {
                title: 'Place market order to buy',
                category: 'Trading',
                shortcutName: 'placeMarketOrderBuy'
            },
            {
                title: 'Place market order to sell',
                category: 'Trading',
                shortcutName: 'placeMarketOrderSell'
            },
            {
                title: 'Place limit order to buy',
                category: 'Trading',
                shortcutName: 'placeLimitOrderBuy'
            },
            {
                title: 'Place limit order to sell',
                category: 'Trading',
                shortcutName: 'placeLimitOrderSell'
            }
        ]
    },
    Alerts: {
        shortcuts: [
            {
                title: 'Create Alert (Open Dialog)',
                category: 'ALERT_DIALOG',
                shortcutName: 'openAlertDialog'
            },
            {
                title: 'Save Changes in Edit Dialog',
                category: 'ALERT_DIALOG',
                shortcutName: 'saveAlertChanges'
            },
            {
                title: 'Next Alert/Event',
                category: 'ALERT_DIALOG',
                shortcutName: 'nextAlert'
            },
            {
                title: 'Previous Alert/Event',
                category: 'ALERT_DIALOG',
                shortcutName: 'previousAlert'
            },
            {
                title: 'Next Alert (Space)',
                category: 'ALERT_DIALOG',
                shortcutName: 'nextAlertSpace'
            },
            {
                title: 'Previous Alert (Space)',
                category: 'ALERT_DIALOG',
                shortcutName: 'previousAlertSpace'
            },
            {
                title: 'Select All Alerts/Events',
                category: 'ALERT_DIALOG',
                shortcutName: 'selectAllAlerts'
            },
            {
                title: 'Select Next Alert/Event',
                category: 'ALERT_DIALOG',
                shortcutName: 'selectNextAlert'
            },
            {
                title: 'Select Previous Alert/Event',
                category: 'ALERT_DIALOG',
                shortcutName: 'selectPreviousAlert'
            },
            {
                title: 'Remove Alert (No Confirmation)',
                category: 'ALERT_DIALOG',
                shortcutName: 'removeAlertNoConfirm'
            }
        ]
    },
    Watchlist: {
        shortcuts: [
            {
                title: 'Add symbol to active watchlist',
                category: 'ADD_TO_WATCHLIST',
                shortcutName: 'addToWatchlist'
            },
            {
                title: 'Change watchlist',
                category: 'WATCHLIST',
                shortcutName: 'changeWatchlist'
            },
            {
                title: 'Next symbol',
                category: 'WATCHLIST',
                shortcutName: 'watchlistNextSymbol'
            },
            {
                title: 'Previous symbol',
                category: 'WATCHLIST',
                shortcutName: 'watchlistPreviousSymbol'
            },
            {
                title: 'Next symbol (Space)',
                category: 'WATCHLIST',
                shortcutName: 'watchlistNextSymbolSpace'
            },
            {
                title: 'Previous symbol (Space)',
                category: 'WATCHLIST',
                shortcutName: 'watchlistPreviousSymbolSpace'
            },
            {
                title: 'Flag/unflag symbol',
                category: 'WATCHLIST',
                shortcutName: 'watchlistFlagUnflagSymbol'
            },
            {
                title: 'Select all symbols',
                category: 'WATCHLIST',
                shortcutName: 'watchlistSelectAllSymbols'
            },
            {
                title: 'Select next symbol',
                category: 'WATCHLIST',
                shortcutName: 'watchlistSelectNextSymbol'
            },
            {
                title: 'Select previous symbol',
                category: 'WATCHLIST',
                shortcutName: 'watchlistSelectPreviousSymbol'
            }
        ]
    },
    Screener: {
        shortcuts: [
            {
                title: 'Next symbol',
                category: 'SCREENER',
                shortcutName: 'nextSymbol'
            },
            {
                title: 'Previous symbol',
                category: 'SCREENER',
                shortcutName: 'previousSymbol'
            },
            {
                title: 'Flag/unflag symbol',
                category: 'SCREENER',
                shortcutName: 'flagUnflagSymbol'
            },
            {
                title: 'Select all symbols',
                category: 'SCREENER',
                shortcutName: 'selectAllSymbols'
            },
            {
                title: 'Select next symbol',
                category: 'SCREENER',
                shortcutName: 'selectNextSymbol'
            },
            {
                title: 'Select previous symbol',
                category: 'SCREENER',
                shortcutName: 'selectPreviousSymbol'
            },
            {
                title: 'Create new screen',
                category: 'SCREENER',
                shortcutName: 'createNewScreen'
            },
            {
                title: 'Save screen',
                category: 'SCREENER',
                shortcutName: 'saveScreen'
            },
            {
                title: 'Load screen',
                category: 'SCREENER',
                shortcutName: 'loadScreen'
            },
            {
                title: 'Save screen as',
                category: 'SCREENER',
                shortcutName: 'saveAsScreen'
            },
            {
                title: 'Export screen',
                category: 'SCREENER',
                shortcutName: 'exportScreen'
            },
            {
                title: 'Rename screen',
                category: 'SCREENER',
                shortcutName: 'renameScreen'
            }
        ]
    },
    'Indicators and drawings': {
        shortcuts: [
            {
                title: 'Copy selected object',
                category: 'Indicators and drawings',
                shortcutName: 'copySelectedObject'
            },
            {
                title: 'Paste object',
                category: 'Indicators and drawings',
                shortcutName: 'pasteObject'
            },
            {
                title: 'Remove selected object',
                category: 'Indicators and drawings',
                shortcutName: 'removeObject'
            },
            {
                title: 'Hide all drawings',
                category: 'Indicators and drawings',
                shortcutName: 'hideAllDrawings'
            },
            {
                title: 'Clone a drawing',
                category: 'Indicators and drawings',
                shortcutName: 'cloneDrawing'
            },
            {
                title: 'Drawings multiselect',
                category: 'Indicators and drawings',
                shortcutName: 'drawingsMultiSelect'
            },
            {
                title: 'Move a drawing horizontally or vertically',
                category: 'Indicators and drawings',
                shortcutName: 'moveDrawingHorizontalVertical'
            },
            {
                title: 'Move selected drawing left',
                category: 'Indicators and drawings',
                shortcutName: 'moveDrawingLeft'
            },
            {
                title: 'Move selected drawing right',
                category: 'Indicators and drawings',
                shortcutName: 'moveDrawingRight'
            },
            {
                title: 'Trendline',
                category: 'Indicators and drawings',
                shortcutName: 'trendline'
            },
            {
                title: 'Horizontal line',
                category: 'Indicators and drawings',
                shortcutName: 'horizontalLine'
            },
            {
                title: 'Horizontal ray',
                category: 'Indicators and drawings',
                shortcutName: 'horizontalRay'
            },
            {
                title: 'Vertical line',
                category: 'Indicators and drawings',
                shortcutName: 'verticalLine'
            },
            {
                title: 'Cross line',
                category: 'Indicators and drawings',
                shortcutName: 'crossline'
            },
            {
                title: 'Fib retracement',
                category: 'Indicators and drawings',
                shortcutName: 'fibRetracement'
            },
            {
                title: 'Rectangle',
                category: 'Indicators and drawings',
                shortcutName: 'rectangle'
            },
            {
                title: 'Circle',
                category: 'Indicators and drawings',
                shortcutName: 'circle'
            },]
    }
}

export const SHORTCUT_GROUPS: Array<{ label: ShortcutCategory; shortcuts: ShortcutEntry[] }> = (
    Object.keys(SHORTCUT_DATA) as ShortcutCategory[]
).map((label) => ({ label, shortcuts: SHORTCUT_DATA[label].shortcuts }))
